/**
 * 화면 켜짐 유지(Screen Wake Lock) — 회의 중 화면이 꺼지지 않게 한다.
 *
 * 호스트는 발화 중 화면이 꺼지면 마이크 상태를 확인할 수 없고, 뷰어는
 * 프로젝터·발표 모드에서 화면이 꺼지면 자막이 사라진 것처럼 보인다. 둘 다
 * "방에 참여해 있는 동안"만 켜져 있으면 되므로, 마이크 시작/방 입장 같은
 * 사용자 제스처 직후에 요청하고 떠날 때 놓는다.
 *
 * API 미지원 브라우저에서는 조용히 'unsupported' 를 반환한다 — 화면은
 * 평소처럼 동작해야 하고, 이 기능이 없다고 콘솔이 실패해서는 안 된다.
 * 권한 거부(NotAllowedError 등)도 같은 규율로 조용히 삼킨다.
 *
 * "켜짐 유지" 자체는 **이 브라우저의 취향**이다(useSourceVeil 과 같은
 * 규율) — 서버 왕복 없이 localStorage 에 저장하고, 저장 실패는 무시한다.
 */

import { useCallback, useEffect, useState } from 'react'

export type WakeLockStatus = 'active' | 'released' | 'unsupported' | 'denied'

interface WakeLockSentinelLike {
  release: () => Promise<void>
  released?: boolean
}

interface NavigatorWithWakeLock {
  wakeLock?: {
    request: (type: 'screen') => Promise<WakeLockSentinelLike>
  }
}

let sentinel: WakeLockSentinelLike | null = null
/**
 * 라우트가 소유하는 의사(意思) 게이트 — "참여 중 ∧ 선호 on" 일 때만 true.
 * 재요청(visibilitychange·센티널 release 이벤트)은 이 값만 본다. 연결이
 * 종결되거나 방을 떠나면 라우트가 clear 해서, 종결 화면이 다시 잠금을
 * 집어가지 못하게 한다(CORRECTION #2 P2).
 */
let wanted = false
/**
 * 요청 세대 — release/clear 가 일어날 때마다 올린다. 요청 시작 시 캡처한
 * 세대가 완료 시점의 세대와 다르면 그 요청은 이미 무효다: 뒤늦게 도착한
 * 센티널을 설치하지 않고 즉시 놓는다(CORRECTION #2 P1).
 */
let generation = 0
/** 진행 중인 요청 — 중복 acquire 호출이 같은 프로미스를 공유하게 한다. */
let pending: Promise<WakeLockStatus> | null = null
/** 진행 중인 요청이 시작된 세대 — 세대가 지난 요청은 공유하지 않는다. */
let pendingGeneration = -1
/** 상태 변화를 듣는 구독자들(브라우저發 release 이벤트 등) — CORRECTION #1 SHOULD 3/4. */
const statusListeners = new Set<(status: WakeLockStatus) => void>()

/** 모든 종료 경로가 같은 콜백으로 상태를 알린다(CORRECTION #2 S3). */
function notifyStatus(status: WakeLockStatus): WakeLockStatus {
  for (const listener of statusListeners) listener(status)
  return status
}

/** 센티널의 release 이벤트를 구독해 상태 갱신 + (원하는 경우) 재요청한다. */
function watchSentinel(s: WakeLockSentinelLike): void {
  const target = s as unknown as { addEventListener?: (type: 'release', cb: () => void) => void }
  target.addEventListener?.('release', () => {
    if (sentinel !== s) return // 이미 새 센티널로 교체된 뒤의 뒤늦은 이벤트
    sentinel = null
    notifyStatus('released')
    if (wanted && document.visibilityState === 'visible') void acquireWakeLock()
  })
}

/**
 * 활성 센티널이 있으면 재요청 없이 그대로 반환하고, 진행 중인 요청이
 * 있으면 그 프로미스를 공유한다(CORRECTION #1 BLOCK 1 — 중복 acquire 로
 * 이전 센티널을 해제 없이 덮어써 누수되던 문제).
 */
export async function acquireWakeLock(): Promise<WakeLockStatus> {
  wanted = true
  if (sentinel && sentinel.released !== true) return 'active'
  // 세대가 같은 요청만 공유한다. release/clear 를 건넌 pending 은 이미
  // 무효(도착하는 센티널을 놓기만 한다)라 그것을 기다리면 이 마운트는
  // 영원히 잠금을 못 얻는다 — StrictMode 이중 마운트가 정확히 이 경로다
  // (CORRECTION #3 P3).
  if (pending && pendingGeneration === generation) return pending

  const myGeneration = generation
  const request = (async (): Promise<WakeLockStatus> => {
    if (typeof navigator === 'undefined') return notifyStatus('unsupported')
    const nav = navigator as unknown as NavigatorWithWakeLock
    if (!nav.wakeLock) return notifyStatus('unsupported')
    try {
      const next = await nav.wakeLock.request('screen')
      if (myGeneration !== generation) {
        // 요청이 도는 동안 release/clear 가 있었다 — 이 센티널은 아무도
        // 원하지 않는다. 설치하지 않고 바로 놓는다(P1 누수 지점).
        try {
          await next.release()
        } catch {
          // 이미 놓인 경우 등 — 무시한다.
        }
        return 'released'
      }
      const stale = sentinel
      sentinel = next
      watchSentinel(next)
      if (stale && stale !== next) {
        try {
          await stale.release()
        } catch {
          // 이미 놓인 경우 등 — 무시한다.
        }
      }
      return notifyStatus('active')
    } catch {
      return notifyStatus('denied')
    }
  })()

  pending = request
  pendingGeneration = myGeneration
  try {
    return await request
  } finally {
    if (pending === request) {
      pending = null
      pendingGeneration = -1
    }
  }
}

export async function releaseWakeLock(): Promise<void> {
  wanted = false
  generation += 1
  const current = sentinel
  sentinel = null
  if (!current) return
  try {
    await current.release()
  } catch {
    // 이미 놓인 경우 등 — 무시한다.
  }
  notifyStatus('released')
}

/**
 * 라우트가 "지금 이 화면이 잠금을 원하는가"(참여 중 ∧ 선호 on)를 선언한다.
 * false 로 내리면 진행 중인 요청까지 무효화되고, 이후 재요청 경로
 * (visibilitychange·센티널 release·무장된 제스처)는 전부 침묵한다.
 */
export function setWakeLockWanted(next: boolean): void {
  if (next) {
    wanted = true
    return
  }
  wanted = false
  generation += 1
}

/** 라우트 테스트·재요청 경로가 보는 의사 게이트. */
export function isWakeLockWanted(): boolean {
  return wanted
}

export function isWakeLockHeld(): boolean {
  if (!sentinel) return false
  return sentinel.released !== true
}

/** 상태 변화 구독 — 브라우저發 release 이벤트를 UI 에 반영한다. */
export function subscribeWakeLockStatus(listener: (status: WakeLockStatus) => void): () => void {
  statusListeners.add(listener)
  return () => statusListeners.delete(listener)
}

/**
 * 탭이 백그라운드로 가면 브라우저가 잠금을 스스로 놓는다(사양 동작) —
 * 다시 보이면(visible) 사용자가 여전히 원하는 경우에만 재요청한다.
 */
export function watchVisibilityReacquire(): () => void {
  const onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible' && wanted) {
      void acquireWakeLock()
    }
  }
  document.addEventListener('visibilitychange', onVisibilityChange)
  return () => document.removeEventListener('visibilitychange', onVisibilityChange)
}

/**
 * 참가자는 마이크를 켜지 않으므로 "방 참여" 시점에 항상 사용자 제스처가
 * 있는 것은 아니다(직통 링크로 자동 입장하면 제스처 없이 마운트된다).
 * 제스처 없이 wakeLock.request() 를 부르면 브라우저가 거부할 수 있으므로,
 * 문서에서 첫 포인터/터치/클릭이 발생하는 순간까지 기다렸다가 그때 요청한다.
 * 셋 중 먼저 온 것 하나로 한 번만 요청하고 나머지 리스너는 정리한다.
 */
export function armGestureAcquire(
  onStatus?: (status: WakeLockStatus) => void,
): () => void {
  let fired = false
  const onGesture = (): void => {
    if (fired) return
    fired = true
    cleanup()
    // 무장한 뒤 연결이 종결되면(종료·강제 퇴장) 라우트가 wanted 를 내린다 —
    // 그때 도착한 첫 터치는 잠금을 집지 않는다(CORRECTION #2 P2).
    if (!wanted) return
    // 미지원·거부도 같은 상태 콜백으로 알린다(CORRECTION #2 S3).
    void acquireWakeLock().then((status) => {
      onStatus?.(status)
      return status
    })
  }
  const cleanup = (): void => {
    document.removeEventListener('pointerdown', onGesture)
    document.removeEventListener('touchstart', onGesture)
    document.removeEventListener('click', onGesture)
  }
  document.addEventListener('pointerdown', onGesture, { once: true })
  document.addEventListener('touchstart', onGesture, { once: true })
  document.addEventListener('click', onGesture, { once: true })
  return cleanup
}

const WAKE_LOCK_PREF_KEY = 'omnitrans:wakeLockPref'

function parseWakeLockPref(raw: unknown): boolean {
  return raw !== '0'
}

/**
 * 켜짐 유지 사용 여부 — 이 브라우저의 취향(기본값 on). 저장 실패는
 * 조용히 무시하고 메모리 상태로만 산다.
 */
export function useWakeLockPref(): { enabled: boolean; setEnabled: (enabled: boolean) => void } {
  const [enabled, setEnabledState] = useState<boolean>(() => {
    if (typeof localStorage === 'undefined') return true
    try {
      return parseWakeLockPref(localStorage.getItem(WAKE_LOCK_PREF_KEY))
    } catch {
      return true
    }
  })

  useEffect(() => {
    const onStorage = (e: StorageEvent): void => {
      if (e.key === WAKE_LOCK_PREF_KEY) setEnabledState(parseWakeLockPref(e.newValue))
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const setEnabled = useCallback((next: boolean) => {
    setEnabledState(next)
    try {
      localStorage.setItem(WAKE_LOCK_PREF_KEY, next ? '1' : '0')
    } catch {
      // 저장 실패는 무시한다 — 이번 세션만 유지된다.
    }
  }, [])

  return { enabled, setEnabled }
}
